import { inject, Injectable } from '@angular/core';
import { Client } from '@df/core/service/client';
import { StylesService } from '@df/core/service/styles';

@Injectable({
    providedIn: 'root'
})
export class StylesServiceDecorator extends StylesService {
    private _client = inject(Client);

    // Performance optimization properties
    private lastScrollTop: number = -1;
    private scrollUpdatePending: boolean = false;
    private rafId: number | null = null;
    private readonly scrollThreshold: number = 1; // Minimum pixels to trigger update

    override appInitialize() {
        super.appInitialize();
        this.setSCrollTop();
    }

    /**
     * Optimized scroll top tracking with 60fps throttling and caching
     * Overrides the base implementation to provide better performance
     */
    protected override setSCrollTop() {
        this.subscriptions.add(
            this._client.obs.scroll.subscribe(() => {
                this.scheduleScrollUpdate();
            })
        );
    }

    /**
     * Schedules a scroll update using requestAnimationFrame for 60fps performance
     * Prevents multiple pending updates and ensures smooth animation
     */
    private scheduleScrollUpdate(): void {
        if (this.scrollUpdatePending) {
            return; // Already scheduled, avoid duplicate RAF calls
        }

        this.scrollUpdatePending = true;

        // Cancel any existing RAF to prevent stacking
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
        }

        this.rafId = requestAnimationFrame(() => {
            this.updateScrollTop();
            this.scrollUpdatePending = false;
            this.rafId = null;
        });
    }

    /**
     * Updates the --scrollTop CSS variable only when the change is significant
     * Minimizes DOM updates and prevents unnecessary reflows/repaints
     */
    private updateScrollTop(): void {
        const currentScrollTop = this._client.getRootElement().scrollTop || 0;

        // Only update if the change exceeds the threshold to avoid excessive DOM updates
        if (Math.abs(currentScrollTop - this.lastScrollTop) >= this.scrollThreshold) {
            this.setCssVar('--scrollTop', `${currentScrollTop}px`);
            this.lastScrollTop = currentScrollTop;
        }
    }

    override ngOnDestroy() {
        // Clean up any pending requestAnimationFrame to prevent memory leaks
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        super.ngOnDestroy();
    }
}
